import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import Citations from 'components/report/Citations'
import Filters from 'components/report/Filters'
import TitlePaper from 'components/report/TitlePaper'
import { claimPaper, toggleFilter, setPublishedRange, setTextMatch, setSort, flushClaimPaperTracker } from 'store/report/actions'
import EditorialNotices from '../misc/EditorialNotices'
import PublicationHistory from '../misc/PublicationHistory'
import CircleSpinner from 'components/misc/CircleSpinner'
import ReportDropdown from 'components/report/ReportDropdown'
import Link from 'components/misc/Link'
import ExportReport from 'components/report/ExportReport'
import { toggleModal } from '../../store/ui/actions'
import {
  selectSectionCounts, selectStatementsCounts,
  selectMinMaxPublishedRange, selectAuthorRelationshipCounts,
  selectCitationsAuthorsCount, selectCitationsJournalsCount,
  selectCitationsPubtypesCount,
  selectReferencesAuthorsCount, selectReferencesJournalsCount,
  selectReferencesPubtypesCount,
  selectYearBins, selectCitationsByPapers
} from 'store/report/selectors'
import EmptyCitations from './EmptyCitations'
import VisualizeButton from 'components/misc/VisualizeButton'
import AddReportToDashboardButton from 'components/misc/AddReportToDashboardButton'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import { SORT_OPTIONS_CITATIONS, SORT_OPTIONS_REFERENCES } from '../../lib/helpers/sort-citation'
import styles from './styles/ReportPanel.css'
import { errorToast, infoToast } from '../../lib/toast'

const ReportHeader = ({ paper, history, user, dispatch }) => {
  const claimPaperLoadTracker = useSelector(({ report }) => report.claimPaperLoadTracker)

  useEffect(() => {
    if (claimPaperLoadTracker?.state === 'loading') {
      infoToast({
        message: 'Claiming paper...',
        durationMs: 1000
      })
    }
    if (claimPaperLoadTracker?.state === 'loaded') {
      toast.success(
        (t) => (
          <span>
            Successfully claimed publication. <Link underline to='/users/me/articles'>View claimed publications.</Link>
          </span>
        ), {
          duration: 3000,

          style: {
            minWidth: '850px',
            color: 'white',
            background: '#339941'
          }
        }
      )
      // Clear the load paper state, so if someone comes back to the report page, the toast doesn't show up.
      dispatch(flushClaimPaperTracker())
    }
    if (claimPaperLoadTracker?.state === 'error') {
      errorToast({
        message: `Error claiming publication: ${claimPaperLoadTracker?.error}`,
        durationMs: 3000
      })
      // Clear the load paper state, so if someone comes back to the report page, the toast doesn't show up.
      dispatch(flushClaimPaperTracker())
    }
  }, [claimPaperLoadTracker])

  return (
    <div className={styles.reportHeader}>
      <div className={styles.leftCol}>
        <TitlePaper paper={paper} />
        <EditorialNotices paper={paper} />
        <PublicationHistory paper={paper} />
      </div>
      <ReportDropdown paper={paper} claimPaper={claimPaper} />
    </div>
  )
}

const CitationFilters = ({ loading }) => {
  const dispatch = useDispatch()
  const filters = useSelector(({ report }) => report.filters)
  const fullReport = useSelector(({ report }) => !report.metadata?.restrictedCitationCount)
  const upgradeReport = useSelector(({ ui }) => !!ui.user)

  const sectionCounts = useSelector(({ report }) => selectSectionCounts(report))
  const statementsCounts = useSelector(({ report }) => selectStatementsCounts(report))

  const totalCitationStatementsCount = useSelector(({ report }) => report.metadata?.totalCitationCount)
  const authorRelationshipCounts = useSelector(({ report }) => selectAuthorRelationshipCounts(report))

  const journalCitationsCounts = useSelector(({ report }) => selectCitationsJournalsCount(report))
  const pubtypeCitationsCounts = useSelector(({ report }) => selectCitationsPubtypesCount(report))
  const authorCitationsCounts = useSelector(({ report }) => selectCitationsAuthorsCount(report))

  const journalReferencesCounts = useSelector(({ report }) => selectReferencesJournalsCount(report))
  const pubtypeReferencesCounts = useSelector(({ report }) => selectReferencesPubtypesCount(report))
  const authorReferencesCounts = useSelector(({ report }) => selectReferencesAuthorsCount(report))

  const minMaxPublishedRange = useSelector(({ report }) => selectMinMaxPublishedRange(report))
  const publishedRange = useSelector(({ report }) => report.filters.publishedRange)

  const showReferences = useSelector(({ report }) => report.filters.showReferences)
  const referencesStatementsCount = useSelector(({ report }) => report.references?.filter(reference => !!reference.type).length)
  const yearBins = useSelector(({ report }) => selectYearBins(report))

  return (
    <Filters
      loading={loading}
      filters={filters}
      fullReport={fullReport}
      upgradeReport={upgradeReport}
      journalCounts={showReferences ? journalReferencesCounts : journalCitationsCounts}
      pubtypeCounts={showReferences ? pubtypeReferencesCounts : pubtypeCitationsCounts}
      authorCounts={showReferences ? authorReferencesCounts : authorCitationsCounts}
      sectionCounts={showReferences ? referencesStatementsCount > 0 && sectionCounts : sectionCounts}
      statementsCounts={!showReferences && statementsCounts}
      totalCitationStatementsCount={!showReferences && totalCitationStatementsCount}
      authorRelationshipCounts={authorRelationshipCounts}
      publishedRange={publishedRange}
      minMaxPublishedRange={minMaxPublishedRange}
      sortOptions={showReferences ? SORT_OPTIONS_REFERENCES : SORT_OPTIONS_CITATIONS}
      setSort={sort => dispatch(setSort(sort))}
      toggleFilter={(key, val) => dispatch(toggleFilter(key, val))}
      toggleModal={(name, to, from) => dispatch(toggleModal(name, to, from))}
      setPublishedRange={(range) => dispatch(setPublishedRange(range))}
      setTextMatch={(text, debounce) => dispatch(setTextMatch(text, debounce))}
      references={showReferences}
      yearBins={yearBins}
    />
  )
}

const CitationsLayout = ({ loading }) => {
  const dispatch = useDispatch()
  const filters = useSelector(({ report }) => report.filters)
  const citingCountsLoading = useSelector(({ report }) => report.citingCountsLoadTracker?.state === 'loading')
  const totalSourcesCount = useSelector(({ report }) => selectCitationsByPapers(report)?.length || 0)
  const referencesCount = useSelector(({ report }) => report.referencesByPapers?.length || 0)
  const paper = useSelector(({ report }) => report.papers[report.paper])
  const hasUser = useSelector(({ ui }) => ui.hasUser)
  const user = useSelector(({ ui }) => ui.user)

  if (loading || totalSourcesCount > 0 || referencesCount > 0) {
    return (
      <div className={styles.mainLayout}>
        <div
          className={classNames(styles.filtersLayout, {
            [styles.filtersMobileLayout]: filters.showMobile
          })}
        >
          <CitationFilters loading={loading || citingCountsLoading} />
          <div className={styles.reportButtons}>
            {hasUser && <VisualizeButton paper={paper} className={styles.reportButton} />}
            <ExportReport className={styles.reportButton} doi={paper?.doi} user={user} toggleModal={(name, to, from) => dispatch(toggleModal(name, to, from))} />
            <AddReportToDashboardButton
              className={styles.reportButton}
              papers={[paper]}
              user={user}
              button
            />
          </div>
        </div>
        <div className={styles.citationsWrapper}>
          <Citations loading={loading} />
        </div>
      </div>
    )
  }

  return <EmptyCitations {...paper} />
}

export const ReportPanel = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const paper = useSelector(({ report }) => report.papers[report.paper])
  const paperLoading = useSelector(({ report }) => report.paperLoadTracker?.state === 'loading')
  const totalSourcesCount = useSelector(({ report }) => selectCitationsByPapers(report)?.length)
  const referencesCount = useSelector(({ report }) => report.referencesByPapers?.length)
  const citationsLoading = useSelector(({ report }) => report.citationsLoadTracker?.state === 'loading')
  const referencesLoading = useSelector(({ report }) => report.referencesLoadTracker?.state === 'loading')
  const user = useSelector(({ ui }) => ui.user)

  const citePanelLoading = citationsLoading || referencesLoading
  const fromOnboarding = history.location.search.includes('fromOnboarding')

  useEffect(() => {
    if (citePanelLoading || !fromOnboarding) {
      return
    }
    if (fromOnboarding) {
      dispatch(toggleModal('whatIsThisReport'))
    }
  }, [citePanelLoading, fromOnboarding])

  useEffect(() => {
    if (!citePanelLoading && totalSourcesCount === 0 && referencesCount > 0) {
      dispatch(toggleFilter('showReferences', true))
    }
  }, [citePanelLoading, referencesCount, totalSourcesCount])

  if (paperLoading) {
    return (
      <div className={styles.loadingLayout}>
        <CircleSpinner citePanelLoading={citePanelLoading} />
      </div>
    )
  }
  return (
    <div className={styles.ReportPanel}>
      <ReportHeader
        paper={paper}
        user={user}
        history={history}
        dispatch={dispatch}
      />
      <div>
        <CitationsLayout loading={citePanelLoading} />
      </div>
    </div>
  )
}

export default ReportPanel
